import { Button, MenuItem, Select, Typography } from '@mui/material';
import { Grid } from '@mui/material';
import ExcelJS from 'exceljs';
import CustomButton from 'lib/designSystem/Button';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  getDebtorConcentrations,
  getFundsConcentrations,
} from '../../../../../../../lib/api/concentrations';
import EnhacedBox from '../../../../../../../lib/designSystem/BigBox';
import CenteredCircularProgress from '../../../../../../../lib/designSystem/CircularProgress';
import HeaderWithActions from '../../../../../../../lib/designSystem/HeaderWithActions';
import DebtorChart from './components/DebtorChart';

const MyWalletConcentrations = () => {
  const [debtorType, setDebtorType] = useState<
    'byCompany' | 'bySector' | 'bySalesSegment'
  >('byCompany');
  const [clientType, setClientType] = useState<
    'byCompany' | 'bySector' | 'bySalesSegment'
  >('byCompany');
  const [startDate, setStartDate] = useState<string>(
    moment().subtract(1, 'months').format('MM/DD/YYYY'),
  );
  const [concentrations, setConcentrations] = useState<any>([]);
  const [fundsConcentrations, setFundsConcentrations] = useState<any>([]);
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();

  const retrieveData = async () => {
    setLoading(true);
    const response = await getDebtorConcentrations(dispatch, startDate);
    if (response) setConcentrations(response);
    const fundsResponse = await getFundsConcentrations(dispatch, startDate);
    if (fundsResponse) setFundsConcentrations(fundsResponse);
    setLoading(false);
  };

  useEffect(() => {
    if (startDate) {
      retrieveData();
    }
  }, [startDate]);

  const exportToXlsx = () => {
    const data = {
      debtor: {
        byCompany: concentrations.debtor.byCompany,
        bySector: concentrations.debtor.bySector,
        bySalesSegment: concentrations.debtor.bySalesSegment,
      },
      client: {
        byCompany: concentrations.client.byCompany,
        bySector: concentrations.client.bySector,
        bySalesSegment: concentrations.client.bySalesSegment,
      },
      funds: fundsConcentrations,
    };

    const workbook = new ExcelJS.Workbook();

    // Helper function to add data to worksheet
    const addDataToWorksheet = (
      worksheet: ExcelJS.Worksheet,
      dataArray: any[],
      title: string,
    ) => {
      // Add title
      worksheet.addRow([title]);
      worksheet.addRow([]); // Empty row

      // Add headers
      worksheet.addRow(['Nombre', 'Valor']);

      // Add data
      dataArray.forEach((item) => {
        worksheet.addRow([item.name, item.value]);
      });

      // Style the title
      const titleRow = worksheet.getRow(1);
      titleRow.font = { bold: true, size: 14 };
      titleRow.getCell(1).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE0E0E0' },
      };

      // Style the headers
      const headerRow = worksheet.getRow(3);
      headerRow.font = { bold: true };
      headerRow.getCell(1).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFF0F0F0' },
      };
      headerRow.getCell(2).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFF0F0F0' },
      };

      // Auto-fit columns
      worksheet.getColumn(1).width = 30;
      worksheet.getColumn(2).width = 15;
    };

    // Create 6 worksheets
    const worksheets = [
      {
        name: 'debtor-byCompany',
        data: data.debtor.byCompany,
        title: 'Concentraciones por Deudor - Por Empresa',
      },
      {
        name: 'debtor-bySector',
        data: data.debtor.bySector,
        title: 'Concentraciones por Deudor - Por Sector',
      },
      {
        name: 'debtor-bySalesSegment',
        data: data.debtor.bySalesSegment,
        title: 'Concentraciones por Deudor - Por Segmento de Ventas',
      },
      {
        name: 'client-byCompany',
        data: data.client.byCompany,
        title: 'Concentraciones por Cliente - Por Empresa',
      },
      {
        name: 'client-bySector',
        data: data.client.bySector,
        title: 'Concentraciones por Cliente - Por Sector',
      },
      {
        name: 'client-bySalesSegment',
        data: data.client.bySalesSegment,
        title: 'Concentraciones por Cliente - Por Segmento de Ventas',
      },
    ];

    worksheets.forEach(({ name, data: worksheetData, title }) => {
      if (worksheetData && worksheetData.length > 0) {
        const worksheet = workbook.addWorksheet(name);
        addDataToWorksheet(worksheet, worksheetData, title);
      }
    });

    // Download the file
    workbook.xlsx.writeBuffer().then((buffer: any) => {
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `concentraciones_${moment().format('YYYY-MM-DD')}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
    });
  };

  if (loading) return <CenteredCircularProgress />;

  return (
    <div>
      <HeaderWithActions
        title="Acá puedes revisar como está colocada tu cartera."
        variant="body1"
        item={
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <Select
              label="Fecha de inicio"
              defaultValue={startDate}
              fullWidth
              style={{ minWidth: '300px', float: 'right' }}
              onChange={(e) => setStartDate(e.target.value)}
            >
              <MenuItem
                value={moment().subtract(1, 'month').format('MM/DD/YYYY')}
              >
                Último mes
              </MenuItem>
              <MenuItem
                value={moment().subtract(3, 'months').format('MM/DD/YYYY')}
              >
                Últimos 3 meses
              </MenuItem>
              <MenuItem
                value={moment().subtract(6, 'months').format('MM/DD/YYYY')}
              >
                Últimos 6 meses
              </MenuItem>
              <MenuItem
                value={moment().subtract(12, 'months').format('MM/DD/YYYY')}
              >
                Últimos 12 meses
              </MenuItem>
            </Select>
            <div>
              <CustomButton
                variant="contained"
                onClick={exportToXlsx}
                style={{ marginLeft: '10px' }}
              >
                Exportar
              </CustomButton>
            </div>
          </div>
        }
      />
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <EnhacedBox>
            <Grid container>
              <Grid item xs={9}>
                <Typography variant="h5">Concentraciones por deudor</Typography>
              </Grid>
              <Grid item xs={3}>
                <Select
                  label="Tipo de concentrado"
                  defaultValue="byCompany"
                  fullWidth
                  style={{ maxWidth: '200px', float: 'right' }}
                  onChange={(e) =>
                    setDebtorType(
                      e.target.value as
                        | 'byCompany'
                        | 'bySector'
                        | 'bySalesSegment',
                    )
                  }
                >
                  <MenuItem value="byCompany">Por empresa</MenuItem>
                  <MenuItem value="bySector">Por sector</MenuItem>
                  <MenuItem value="bySalesSegment">
                    Por segmento de ventas
                  </MenuItem>
                </Select>
              </Grid>
            </Grid>
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
              }}
            >
              <DebtorChart type={debtorType} data={concentrations.debtor} />
            </div>
          </EnhacedBox>
        </Grid>
        <Grid item xs={6}>
          <EnhacedBox>
            <Grid container>
              <Grid item xs={9}>
                <Typography variant="h5">
                  Concentraciones por cliente
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <Select
                  label="Tipo de concentrado"
                  defaultValue="byCompany"
                  fullWidth
                  style={{ maxWidth: '200px', float: 'right' }}
                  onChange={(e) =>
                    setClientType(
                      e.target.value as
                        | 'byCompany'
                        | 'bySector'
                        | 'bySalesSegment',
                    )
                  }
                >
                  <MenuItem value="byCompany">Por empresa</MenuItem>
                  <MenuItem value="bySector">Por sector</MenuItem>
                  <MenuItem value="bySalesSegment">
                    Por segmento de ventas
                  </MenuItem>
                </Select>
              </Grid>
            </Grid>
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
              }}
            >
              <DebtorChart type={clientType} data={concentrations.client} />
            </div>
          </EnhacedBox>
        </Grid>
        <Grid item xs={12}>
          <div style={{ paddingBottom: 100 }}>
            <EnhacedBox>
              <Typography variant="h5">Concentraciones por fondos</Typography>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                }}
              >
                <DebtorChart type={'byCompany'} data={fundsConcentrations} />
              </div>
            </EnhacedBox>
          </div>
        </Grid>
      </Grid>
    </div>
  );
};

export default MyWalletConcentrations;
